import { Spinner } from '@getethos/ethos-design-system-v2';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import styles from '../styles.module.scss';
import { ETIAnswerV1, ETIFormData, QuestionFormData } from '../types';
import { generateSectionNumbering } from '../utils/numbering';
import drawerStyles from './ETIBuilderDrawer.module.scss';
import ETIBuilderQuestionRow from './ETIBuilderQuestionRow';

export interface ETIBuilderFormProps {
  control: Control<Omit<ETIFormData, 'taskId' | 'status'>>;
  setValue?: UseFormSetValue<Omit<ETIFormData, 'taskId' | 'status'>>;
  className?: string;
  isDisabled: boolean;
  isReadOnly: boolean;
  isLoading: boolean;
  answers: ETIAnswerV1[];
  isVersion2: boolean;
  onAddQuestionReady?: (addQuestion: () => void) => void;
}

const ETIBuilderForm = ({
  control,
  setValue,
  className,
  isDisabled,
  isReadOnly,
  isLoading,
  answers,
  isVersion2,
  onAddQuestionReady,
}: ETIBuilderFormProps) => {
  const { fields, append, remove } = useFieldArray({
    name: 'questions',
    control,
  });

  const watchedQuestions = useWatch({ control, name: 'questions' }) ?? [];
  const numbering = useMemo(
    () => generateSectionNumbering(watchedQuestions as any),
    [watchedQuestions],
  );

  // Fixed columns: #, Type, Section, Question, Options, Filter = 6.
  // + Actions when editable; + Answer when read-only V1; otherwise no extra column.
  const columnCount = 6 + (!isReadOnly || !isVersion2 ? 1 : 0);

  const handleOnAddClick = useCallback(() => {
    const current = (watchedQuestions ?? []) as QuestionFormData[];
    const lastSection = current[current.length - 1]?.section ?? '';
    append({
      question: '',
      questionType: 'text',
      section: lastSection,
      optionsRaw: '',
      filterRaw: '',
      options: undefined,
      filter: undefined,
    });
  }, [append, watchedQuestions]);

  useEffect(() => {
    onAddQuestionReady?.(handleOnAddClick);
  }, [handleOnAddClick, onAddQuestionReady]);

  const handleRemoveQuestion = useCallback(
    (indexToRemove: number) => {
      const currentQuestions = (watchedQuestions ?? []) as any[];
      const deletedQuestion = currentQuestions[indexToRemove];
      if (!deletedQuestion) return;

      const deletedSection = deletedQuestion.section ?? '';

      let deletedDisplayNumber = 0;
      for (let i = 0; i <= indexToRemove; i++) {
        if (currentQuestions[i]?.section === deletedSection) {
          deletedDisplayNumber++;
        }
      }

      const filterUpdates: {
        index: number;
        filter: any;
        filterRaw: string;
      }[] = [];

      for (let i = 0; i < currentQuestions.length; i++) {
        if (i === indexToRemove) continue;
        const question = currentQuestions[i];
        if (
          question.section === deletedSection &&
          question.filter &&
          typeof question.filter === 'object' &&
          !Array.isArray(question.filter)
        ) {
          const updatedFilter = { ...question.filter };
          let hasChanges = false;
          for (const [key, value] of Object.entries(updatedFilter)) {
            const refNum = parseInt(key, 10);
            if (isNaN(refNum)) continue;
            if (refNum === deletedDisplayNumber) {
              delete updatedFilter[key];
              hasChanges = true;
            } else if (refNum > deletedDisplayNumber) {
              delete updatedFilter[key];
              updatedFilter[refNum - 1] = value;
              hasChanges = true;
            }
          }
          if (hasChanges) {
            const isEmpty = Object.keys(updatedFilter).length === 0;
            filterUpdates.push({
              index: i,
              filter: isEmpty ? undefined : updatedFilter,
              filterRaw: isEmpty ? '' : JSON.stringify(updatedFilter),
            });
          }
        }
      }

      filterUpdates.forEach(update => {
        setValue?.(`questions.${update.index}.filter`, update.filter, {
          shouldDirty: true,
        });
        setValue?.(`questions.${update.index}.filterRaw`, update.filterRaw, {
          shouldDirty: true,
        });
      });

      remove(indexToRemove);
    },
    [watchedQuestions, remove, setValue],
  );

  return (
    <div className={className}>
      <Table component={Paper} className={drawerStyles.tableRoot}>
        <TableHead>
          <TableRow>
            <TableCell className={styles.NumberCell}>#</TableCell>
            <TableCell className={styles.QuestionTypeCell}>Type</TableCell>
            <TableCell className={styles.SectionCell}>Section</TableCell>
            <TableCell className={styles.Cell}>Question</TableCell>
            <TableCell className={styles.Cell}>Options</TableCell>
            <TableCell className={styles.Cell}>Filter</TableCell>
            {!isReadOnly ? (
              <TableCell className={styles.ActionsCell} align="center">
                Actions
              </TableCell>
            ) : !isVersion2 ? (
              <TableCell className={styles.Cell}>Answer</TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, index) => (
            <ETIBuilderQuestionRow
              onRemove={() => handleRemoveQuestion(index)}
              index={index}
              key={field.id}
              control={control}
              setValue={setValue}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              answers={answers}
              isVersion2={isVersion2}
              questionNumber={numbering[index]?.number}
            />
          ))}
          {isLoading && (
            <TableRow>
              <TableCell align="center" colSpan={columnCount}>
                <Spinner />
              </TableCell>
            </TableRow>
          )}
          {!isLoading && fields.length === 0 && (
            <TableRow sx={{ height: '60px' }}>
              <TableCell align="center" colSpan={columnCount}>
                No questions
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ETIBuilderForm;
